import { PencilIcon, PlusCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import _, { isEmpty, isEqual } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Controller,
  FieldValues,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { EmailListFieldsRecordType, emailQueueListName } from "src/api/config/email.config";
import { myTaskMeetingsListName } from "src/api/config/meetings.config";
import {
  myTasksDepartmentsListName,
  myTasksDependListName,
  myTasksLocationsListName,
} from "src/api/config/myTasksLists.config";
import { DrawerComponent, Modal } from "src/components/DialogComponent";
import SortIcon from "src/components/ui-icons/table/SortIcon";
import { SearchUI } from "src/components/ui/Search";
import { TableHeaderColumnProps } from "src/components/ui/table/Table";
import TGButton from "src/components/ui/TGButton";
import { Option, TGSelectPickerByList } from "src/components/ui/TGSelect";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { addItemSuccess, updateItem } from "src/modules/spItems/actions";
import { addItemRequest, getItemsRequest } from "src/modules/spItems/api";
import { useIsRegionHiddenExemptPage } from "src/pages/DashboardPage/mytasks/utils/regionHiddenExempt";
import { selectJustAddedItem } from "src/modules/spItems/selectors";
import { selectCurrentUser, selectUsers } from "src/modules/users/selector";
import { TableColumnWithIcon } from "src/pages/DashboardPage/acadia/mock";
import { emailAPI } from "src/utils/api.utils";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { compareObjects } from "src/utils/object.utils";
import { nameFromEmail } from "src/utils/users.utils";
import { twMerge } from "tailwind-merge";
import { Participant, PersonType, Role } from "../type";
import { DownloadPDFButton } from "./DownloadPDFTOR";
import { UserRoleTypes } from "./MeetingCard";
import { SearchInputEmployee } from "./SearchInputEmployee";
import { useUsers } from "src/hooks/useUsers";
import AdditionalMeetingFields, { FieldType } from "./AdditionalMeetingFields";
import { AdditionalFieldsProvider } from "../providers/additionalFieldsProvider";
import { autoCloseModal } from "src/constants";

type MeetingFormValues = {
  name: string;
  participants: Participant[];
  frequency: Option[];
  location: Option[];
  department: Option[];

  visibility: Option[];
  tor: {
    goal: string;
    owner: string;
    participants: string;
    period: string;
    introduction: string;
    results: string;
    kpi: string;
    meetingsPlan: any[];
  };
  additionalFields: FieldType[];
};

export function CreateMeetingButton() {
  const [open, setOpen] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);

  const closeDialog = () => {
    setOpen(false);
  };

  function toggleDialog(value: boolean) {
    setOpen(value);
  }

  return (
    <>
      <TGButton icon={<PlusCircleIcon width={20} height={20} />} onClick={() => setOpen(!open)} color="bluedark">
        Создать совещание
      </TGButton>
      <DrawerComponent open={open} zIndex={50} percentage={"93"} toggleDrawer={() => setOpenCancelModal(true)}>
        <FlexCol className="p-10 shrink custom-form">
          <h1 className="mb-8 text-black">Создание совещания</h1>

          <CreateMeetingForm
            toggleDialog={toggleDialog}
            onSubmitResult={(result: any) => console.log("result", result)}
            defaultValues={{
              name: "",
              participants: [],
              frequency: [],
              location: [],
              department: [],
              visibility: [{ id: "1", name: "Для всех сотрудников" }],
              tor: {
                goal: "",
                owner: "",
                participants: "",
                period: "",
                introduction: "",
                results: "",
                kpi: "",
                meetingsPlan: [],
              },
              additionalFields: [],
            }}
          />
        </FlexCol>
        <CancelModal
          open={openCancelModal}
          toggleCancel={setOpenCancelModal}
          leftButton={() => {
            setOpenCancelModal(false);
            closeDialog();
          }}
          rightButton={() => {
            setOpenCancelModal(false);
          }}
        />
      </DrawerComponent>
    </>
  );
}

export const frequencyList = [
  { id: "1", name: "Ежесменная" },
  { id: "2", name: "Ежедневная" },
  { id: "3", name: "Еженедельная" },
  { id: "4", name: "Ежемесячная" },
  { id: "5", name: "Ежеквартальная" },
  { id: "6", name: "Ежегодная" },
];

export const frequencyCheckList = [
  { id: "1", name: "Смена" },
  { id: "2", name: "День" },
  { id: "3", name: "Неделя" },
  { id: "4", name: "Месяц" },
  { id: "5", name: "Квартал" },
  { id: "6", name: "Год" },
  { id: "6", name: "По необходимости" },
];

export const visibilityList = [
  { id: "1", name: "Для всех сотрудников" },
  { id: "2", name: "Только для участников совещания" },
];

const { sendEmail, createEmailMeetingBodyByTemplate } = emailAPI();

export const CreateMeetingForm = ({
  toggleDialog,
  defaultValues,
  type = "create",
  meetingID,
  disabled,
  role,
  onSubmitResult,
}: {
  toggleDialog: (value: boolean) => void;
  defaultValues: MeetingFormValues;
  type?: "create" | "edit";
  meetingID?: number;
  disabled?: boolean;
  role?: UserRoleTypes;
  onSubmitResult: (result: FieldValues) => void;
}) => {
  const dispatch = useAppDispatch();
  const formRef = useRef<HTMLFormElement>(null);
  const prevLocationOptionRef = useRef<Option | null>(null);

  // Локации/отделы/зависимости для этой формы загружаются напрямую (минуя общий Redux-кэш по listName),
  // так как на странице "Совещания" логика скрытия локации "Регион" не должна применяться,
  // а тот же кэш используется другими страницами, где скрытие обязательно.
  const isRegionHiddenExempt = useIsRegionHiddenExemptPage();
  const [locationsData, setLocationsData] = useState<{ ID: number; Title: string }[]>([]);
  const [departmentsData, setDepartmentsData] = useState<{ ID: number; Title: string }[]>([]);
  const [dependsData, setDependsData] = useState<any[]>([]);

  const justAddedItemID = useAppSelector((state) => selectJustAddedItem(state, myTaskMeetingsListName));
  const [query, setQuery] = useSearchParams();
  const isMobile = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(max-width: 768px)")?.matches ?? window.innerWidth <= 768;
  }, []);

  const currentUser = useAppSelector((state) => selectCurrentUser(state));
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isMeetingModified, setIsMeetingModified] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openSaveModal, setOpenSaveModal] = useState(false);

  const methods = useForm<MeetingFormValues>({
    defaultValues: defaultValues,
    disabled: disabled,
  });

  const {
    handleSubmit,
    control,
    watch,
    reset,
    getValues,
    setValue,
    formState: { dirtyFields },
  } = methods;

  const onSubmit = async (data: FieldValues) => {
    const { owners, participants } = data.participants.reduce(
      (acc: { owners: string; participants: string }, cur: any) => {
        if (cur.role[0].id === "1") {
          acc.owners = acc.owners ? acc.owners + ", " + cur.user.Email : cur.user.Email;
        } else {
          acc.participants = acc.participants ? acc.participants + ", " + cur.user.Email : cur.user.Email;
        }

        return acc;
      },
      { owners: "", participants: "" } as { owners: string; participants: string },
    );

    const requestBody = {
      Department: +data.department[0].id,
      Frequency: data.frequency[0].name,
      Location: +data.location[0].id,
      Title: data.name,
      Owners: owners,
      Participants: participants,
      isPrivate: data.visibility[0].id === "2",
      Tor: JSON.stringify(data.tor),
      AdditionalFields: JSON.stringify(data.additionalFields),
    };

    const { createMeeting, assigningOwnerInMeeting, inviteParticipantToMeeting } = createEmailMeetingBodyByTemplate();

    if (type === "create") {
      const addMeetingID = (await addItemRequest({
        listName: myTaskMeetingsListName,
        body: requestBody,
        // updateReducer: true,
      })) as number | string;

      dispatch(addItemSuccess({ ID: addMeetingID, listName: myTaskMeetingsListName }));

      const emailsData = data.participants.reduce((acc: Promise<any>[], participant: Participant) => {
        const userName = nameFromEmail(participant.user.Email);

        if (participant.user.Email.toLowerCase() !== currentUser.email.toLowerCase()) {
          if (participant.role[0].id === "1") {
            const createMeetingUserEmail = assigningOwnerInMeeting(
              { title: data.name, id: addMeetingID },
              nameFromEmail(currentUser.email),
              userName,
            );

            const emailBody: Partial<EmailListFieldsRecordType[typeof emailQueueListName]> = {
              Body: createMeetingUserEmail,
              DateToBeSentAt: new Date(),
              Title: "Совещание",
              Subject: "Мои Задачи | Назначение владельца за совещание",
              Recepients: participant.user.Email.toLowerCase(),
            };

            acc.push(sendEmail(emailBody));
          } else {
            const createMeetingUserEmail = createMeeting(
              { title: data.name, id: addMeetingID },
              nameFromEmail(currentUser.email),
              userName,
            );

            const emailBody: Partial<EmailListFieldsRecordType[typeof emailQueueListName]> = {
              Body: createMeetingUserEmail,
              DateToBeSentAt: new Date(),
              Title: "Совещание",
              Subject: "Мои Задачи | Создание нового совещания",
              Recepients: participant.user.Email.toLowerCase(),
            };

            acc.push(sendEmail(emailBody));
          }
        }
        return acc;
      }, []) as Promise<any>[];

      await Promise.all(emailsData);
    } else {
      if (meetingID) {
        dispatch(
          updateItem({
            listName: myTaskMeetingsListName,
            body: { ID: meetingID, ...requestBody },
            // updateReducer: true,
          }),
        );

        if (defaultValues && defaultValues.participants.length) {
          const filterNewParticipants = data.participants.filter((participant: Participant) => {
            const isCurrentUser = participant.user.Email.toLowerCase() === currentUser.email.toLowerCase();

            const alreadyExists = defaultValues.participants.some(
              (p: Participant) =>
                participant.user.Email.toLowerCase() === p.user.Email.toLowerCase() &&
                participant.role[0].id === p.role[0].id,
            );

            // Условие: исключаем текущего пользователя и существующих участников
            return !isCurrentUser && !alreadyExists;
          });

          const emailsData = filterNewParticipants.reduce((acc: Promise<any>[], participant: Participant) => {
            const userName = nameFromEmail(participant.user.Email);

            if (participant.user.Email.toLowerCase() !== currentUser.email.toLowerCase()) {
              if (participant.role[0].id === "1") {
                const createMeetingUserEmail = assigningOwnerInMeeting(
                  { id: meetingID, title: data.name },
                  nameFromEmail(currentUser.email),
                  userName,
                );
                const emailBody: Partial<EmailListFieldsRecordType[typeof emailQueueListName]> = {
                  Body: createMeetingUserEmail,
                  DateToBeSentAt: new Date(),
                  Title: "Совещание",
                  Subject: "Мои Задачи | Назначение владельца за совещание",
                  Recepients: participant.user.Email.toLowerCase(),
                };

                acc.push(sendEmail(emailBody));
              } else {
                const createMeetingUserEmail = inviteParticipantToMeeting(
                  { id: meetingID, title: data.name },
                  nameFromEmail(currentUser.email),
                  userName,
                );
                const emailBody: Partial<EmailListFieldsRecordType[typeof emailQueueListName]> = {
                  Body: createMeetingUserEmail,
                  DateToBeSentAt: new Date(),
                  Title: "Совещание",
                  Subject: "Мои Задачи | Добавление участника совещания",
                  Recepients: participant.user.Email.toLowerCase(),
                };

                acc.push(sendEmail(emailBody));
              }
            }
            return acc;
          }, []) as Promise<any>[];

          await Promise.all(emailsData);
        }
      }
    }

    onSubmitResult(data);
  };

  function submitСloseSuccessSaveMeeting() {
    if (!!justAddedItemID && type === "create") {
      setOpenSaveModal(false);
      cancel();

      // Ensure we open the correct meeting card modal for the current device.
      // On mobile we should use `meetingCardMobile`, otherwise desktop meeting card may open on iPhone.
      query.delete("meetingCard");
      query.delete("meetingCardMobile");
      query.set(isMobile ? "meetingCardMobile" : "meetingCard", justAddedItemID.toString());
      setQuery(query);
    }

    setOpenSaveModal(false);
  }

  function cancel() {
    toggleDialog(false);
  }

  const watchValues = watch();

  useEffect(() => {
    if (type === "edit") {
      const tor = getValues("tor");
      const values = getValues();

      const defaultTor = defaultValues ? defaultValues.tor : {};
      const defaultV = defaultValues ? defaultValues : {};
      const isTorValuesEqual = isEqual(tor, defaultTor);
      const isValuesEqual = isEqual(values, defaultV);
      const isAdditionalFieldsEqual = isEqual(values.additionalFields, defaultValues.additionalFields);

      if (!isTorValuesEqual || !isValuesEqual || !isAdditionalFieldsEqual) {
        setIsMeetingModified(true);
      } else {
        setIsMeetingModified(false);
      }
    }
  }, [watchValues, JSON.stringify(dirtyFields), JSON.stringify(defaultValues)]);

  // useEffect(() => {
  //   if (type === "edit") {
  //     const participants = getValues("participants");

  //     if (participants) {
  //       setIsMeetingModified(true);
  //     } else {
  //       setIsMeetingModified(false);
  //     }
  //   }
  // }, [watch("participants")]);

  // На странице "Совещания" логика скрытия локации "Регион" не должна применяться —
  // создание совещания должно позволять выбрать любую локацию/отдел независимо от роли пользователя.
  useEffect(() => {
    getItemsRequest({ listName: myTasksLocationsListName, skipHiddenLocationFilter: isRegionHiddenExempt }).then(
      ({ items }) => setLocationsData(items as any),
    );
  }, [isRegionHiddenExempt]);

  useEffect(() => {
    if (dependsData.length) {
      const departamentIDs = dependsData.reduce(
        (acc, d) => (d.Departament.get_lookupId() ? acc.concat(d.Departament.get_lookupId()) : acc),
        [],
      );

      const departamentUniqIDs = [...new Set(departamentIDs)];

      if (departamentUniqIDs.length) {
        getItemsRequest({
          listName: myTasksDepartmentsListName,
          filters: { ID: departamentUniqIDs },
          skipHiddenLocationFilter: isRegionHiddenExempt,
        }).then(({ items }) => setDepartmentsData(items as any));
      }
    } else {
      getItemsRequest({ listName: myTasksDepartmentsListName, skipHiddenLocationFilter: isRegionHiddenExempt }).then(
        ({ items }) => setDepartmentsData(items as any),
      );
    }
  }, [dependsData, isRegionHiddenExempt]);

  useEffect(() => {
    getItemsRequest({ listName: myTasksDepartmentsListName, skipHiddenLocationFilter: isRegionHiddenExempt }).then(
      ({ items }) => setDepartmentsData(items as any),
    );
  }, [isRegionHiddenExempt]);

  useEffect(() => {
    const [location] = getValues("location");

    if (location) {
      getItemsRequest({
        listName: myTasksDependListName,
        filters: { Location: location.id },
        skipHiddenLocationFilter: isRegionHiddenExempt,
      }).then(({ items }) => setDependsData(items as any));

      if (prevLocationOptionRef.current && prevLocationOptionRef.current?.id !== location.id) {
        setValue("department", []);
      }

      prevLocationOptionRef.current = location;
    } else {
      setValue("department", []);
    }
  }, [watch("location"), isRegionHiddenExempt]);

  return (
    <FormProvider {...methods}>
      <form
        ref={formRef}
        onSubmit={handleSubmit(async (data) => {
          // Выполняем onSubmit
          setSubmitLoading(true);
          try {
            await onSubmit(data);
            setOpenSaveModal(true);
          } catch (error) {
            console.error(error);
          } finally {
            setSubmitLoading(false);
          }
        })}
        className="w-full"
      >
        {/* Название совещания */}
        <div className="mb-5">
          <Controller
            rules={{ required: true }}
            render={({
              field: { onChange, onBlur, value, name, ref, disabled },
              fieldState: { invalid, isTouched, isDirty, error },
              formState,
            }) => (
              <SearchUI
                search={false}
                type="text"
                require={true}
                error={!!error}
                disabled={disabled}
                placeholder={"Введите название совещания"}
                defaultValue={value}
                onClick={onChange}
                label={"Название совещания"}
                // inputClass={"appearance-none"}
                labelClass="font-normal text-black"
                resetHandle={() => onChange("")}
              />
            )}
            name="name"
            control={control}
          />
        </div>
        <div className="space-y-5">
          <section className="grid grid-cols-2 grid-rows-2 gap-5 mb-5 custom-selects">
            <div>
              <Controller
                rules={{ required: true }}
                render={({
                  field: { onChange, onBlur, value, name, ref, disabled },
                  fieldState: { invalid, isTouched, isDirty, error },
                  formState,
                }) => (
                  <TGSelectPickerByList
                    list={locationsData.map((d) => ({ id: d.ID.toString(), name: d.Title }))}
                    values={value}
                    onChange={onChange}
                    fetchKey="Region"
                    classNames={{ selectClassName: "!px-5", labelClassName: "text-base font-normal" }}
                    searchPlaceholder="Найти локацию"
                    dataField={{
                      placeholder: "Выберите локацию",
                      type: "peoplePicker",
                      require: true,
                      disabled: disabled,
                      title: "Локация",
                      width: "w-full",
                      key: "location",
                      single: true,
                    }}
                    errors={{ location: !!error }}
                  />
                )}
                name="location"
                control={control}
              />
            </div>
            <div>
              <Controller
                rules={{ required: true }}
                render={({
                  field: { onChange, onBlur, value, name, ref, disabled },
                  fieldState: { invalid, isTouched, isDirty, error },
                  formState,
                }) => (
                  <TGSelectPickerByList
                    list={departmentsData.map((d) => ({ id: d.ID.toString(), name: d.Title }))}
                    values={value}
                    onChange={onChange}
                    fetchKey="Department"
                    classNames={{ selectClassName: "!px-5", labelClassName: "text-base font-normal" }}
                    searchPlaceholder="Найти отдел"
                    dataField={{
                      placeholder: "Выберите отдел",
                      type: "peoplePicker",
                      require: true,
                      title: "Отдел",
                      width: "w-full",
                      disabled: disabled || (getValues("location") && !getValues("location").length),
                      key: "department",
                      single: true,
                    }}
                    errors={{ department: !!error }}
                  />
                )}
                name="department"
                control={control}
              />
            </div>
            <div>
              <Controller
                rules={{ required: true }}
                render={({
                  field: { onChange, onBlur, value, name, ref, disabled },
                  fieldState: { invalid, isTouched, isDirty, error },
                  formState,
                }) => (
                  <TGSelectPickerByList
                    values={value}
                    onChange={onChange}
                    fetchKey="Frequencies"
                    classNames={{
                      selectClassName: "!px-5",
                      labelClassName: "text-base font-normal",
                    }}
                    searchPlaceholder="Найти периодичность"
                    dataField={{
                      placeholder: "Выберите периодичность",
                      type: "peoplePicker",
                      require: true,
                      title: "Периодичность",
                      width: "w-full",
                      disabled: disabled,
                      key: "frequency",
                      single: true,
                    }}
                    list={frequencyList}
                    errors={{ frequency: !!error }}
                  />
                )}
                name="frequency"
                control={control}
              />
            </div>
            <div>
              <Controller
                rules={{ required: true }}
                render={({
                  field: { onChange, onBlur, value, name, ref, disabled },
                  fieldState: { invalid, isTouched, isDirty, error },
                  formState,
                }) => (
                  <TGSelectPickerByList
                    values={value}
                    onChange={onChange}
                    fetchKey="visibility"
                    classNames={{
                      selectClassName: "!px-5",
                      labelClassName: "text-base font-normal",
                    }}
                    searchPlaceholder="Найти периодичность"
                    dataField={{
                      placeholder: "Выберите",
                      type: "peoplePicker",
                      require: true,
                      title: "Видимость совещания",
                      width: "w-full",
                      disabled: disabled,
                      key: "visibility",
                      single: true,
                    }}
                    list={visibilityList}
                    errors={{ visibility: !!error }}
                  />
                )}
                name="visibility"
                control={control}
              />
            </div>
          </section>
          <section>
            <h3 className="text-[#637381] font-medium mb-2.5">Участники совещания</h3>
            <Participants formType={type} />
          </section>
          <section>
            <h3 className="text-[#637381] font-medium mb-2.5">Регламент совещания (TOR)</h3>
            <RegulationsTor />
          </section>
          <section>
            <h3 className="text-[#637381] font-medium mb-2.5">Дополнительные поля для действий</h3>
            <AdditionalFieldsProvider>
              <AdditionalMeetingFields />
            </AdditionalFieldsProvider>
          </section>
          {!disabled && (
            <FlexRow className="justify-between mt-10">
              {type === "create" ? (
                <>
                  <TGButton type="button" onClick={() => setOpenCancelModal(true)}>
                    Отмена
                  </TGButton>
                  <TGButton type="submit" color="bluedark">
                    Создать совещание
                  </TGButton>
                </>
              ) : (role === "owner" || currentUser.isAdmin) && isMeetingModified ? (
                <>
                  <TGButton
                    type="button"
                    onClick={() => {
                      reset(defaultValues);
                    }}
                  >
                    Отменить изменения
                  </TGButton>
                  <TGButton disabled={submitLoading} type="submit" color="bluedark">
                    Сохранить изменения
                  </TGButton>
                </>
              ) : null}
            </FlexRow>
          )}
        </div>
        {/* Отдел */}
      </form>
      {/* Отмена */}
      <CancelModal
        open={openCancelModal}
        toggleCancel={setOpenCancelModal}
        leftButton={() => {
          setOpenCancelModal(false);
          cancel();
        }}
        rightButton={() => {
          setOpenCancelModal(false);
        }}
      />
      {/* Удаление совещания */}
      <Modal
        zIndex={70}
        open={openSaveModal}
        toggleDialog={(state) => setOpenSaveModal(state)}
        modalClassName="min-h-0 max-w-[600px]"
        autoClose={autoCloseModal}
        onAutoClose={() => {
          submitСloseSuccessSaveMeeting();
        }}
      >
        <header className="px-10 pb-4">
          <h2>Вы успешно сохранили форму</h2>
        </header>

        <div className="px-10 py-5 grow">
          <p>Форма сохранена со всеми изменениями</p>
        </div>

        <footer className="flex justify-center px-10 pt-10 mt-4 border-t">
          <TGButton
            type="button"
            color="bluedark"
            onClick={(e) => {
              e.stopPropagation();
              submitСloseSuccessSaveMeeting();
            }}
          >
            Закрыть
          </TGButton>
        </footer>
      </Modal>
    </FormProvider>
  );
};

function CancelModal({
  open,
  rightButtonTitle,
  toggleCancel,
  leftButton,
  rightButton,
}: {
  open: boolean;
  rightButtonTitle?: string;
  toggleCancel: (state: boolean) => void;
  leftButton: (state: any) => void;
  rightButton: (state: any) => void;
}) {
  return (
    <Modal zIndex={70} open={open} toggleDialog={(state) => toggleCancel(state)} modalClassName="min-h-0 max-w-[600px]">
      <header className="px-10 pb-4">
        <h2>Подтвердите действие</h2>
      </header>

      <div className="px-10 py-5 grow">
        <p>
          Вы уверены, что хотите закрыть окно?
          <br />
        </p>
        <p>
          Вся введенная вами информация будет сброшена.
          <br />
        </p>
        <p>Заполните все обязательные поля, чтобы сохранить совещание.</p>
      </div>

      <footer className="flex flex-col gap-2 px-10 pt-10 mt-4 border-t sm:flex-row sm:justify-between">
        <TGButton type="button" onClick={leftButton}>
          Закрыть без сохранения
        </TGButton>
        <TGButton type="button" color="bluedark" onClick={rightButton}>
          {rightButtonTitle || "Вернуться к совещанию"}
        </TGButton>
      </footer>
    </Modal>
  );
}

function Participants({ formType }: { formType: "create" | "edit" }) {
  const selectedParticipant = useRef<{
    index: number;
    user: PersonType;
    oldRole: Role[]; // Сохраняем текущую роль
    newRole: Role[]; // Сохраняем выбранную роль
  } | null>(null);

  const {
    control,
    setValue,
    formState: { disabled },
  } = useFormContext();

  const fieldFormArray = useFieldArray({
    control,
    name: "participants",
    // shouldUnregister: true,
  });

  const [openConfirmChangeRoleModal, setOpenConfirmChangeRoleModal] = useState(false);

  const { fields, append, remove, update } = fieldFormArray;

  // Отслеживаем последнюю версию данных
  const participants = useWatch({ control, name: "participants" }) as Participant[];

  const currentUser = useAppSelector((state) => selectCurrentUser(state));

  function addParticipant() {
    const newParticipant = {
      user: null,
      role: [{ id: "2", name: "Участник" }],
    };
    append(newParticipant);
    setValue("participants", [...participants, newParticipant]);
  }

  function removeParticipantById(id: string) {
    const index = fields.findIndex((field: { id: string }) => field.id === id);
    if (index !== -1) {
      remove(index);
    }
  }

  const handleRoleChange = (ev: Role[], index: number) => {
    const { user, role } = fields[index] as Participant; // Получаем участника по индексу

    if (role[0].id === ev[0].id) {
      return;
    }

    // Проверяем условия для открытия модального окна
    if (user && currentUser.email === user.Email && ev[0] && ev[0].id === "2") {
      setOpenConfirmChangeRoleModal(true); // открываем модалку
      selectedParticipant.current = {
        index,
        user: user,
        oldRole: role, // Сохраняем текущую роль
        newRole: ev, // Сохраняем выбранную роль
      };
    } else {
      // Обновляем роль участника, если условия не совпали
      update(index, { ...fields[index], role: ev });
    }
  };

  const handleUserChange = (user: PersonType | null, index: number) => {
    // Обновляем роль участника, если условия не совпали
    update(index, { ...fields[index], user: user });
  };

  function submitChangeRole() {
    if (selectedParticipant.current) {
      const { index, user, newRole } = selectedParticipant.current;
      // Обновляем роль участника по индексу
      update(index, { user, role: newRole });

      // Закрываем модалку после изменения
      setOpenConfirmChangeRoleModal(false);
    }
  }

  function closeConfirmChangeRoleModal(state: boolean) {
    if (!state && selectedParticipant.current) {
      const { index, user, oldRole } = selectedParticipant.current;
      update(index, { user, role: oldRole });
    }
    setOpenConfirmChangeRoleModal(state);
  }

  // Автоматическое добавление текущего пользователя как владельца
  useEffect(() => {
    if (formType === "create" && !participants.length && currentUser.email) {
      append({
        user: {
          Avatar: currentUser.avatar || null,
          ShortName: `${currentUser.displayName.split(" ")[0] + currentUser.displayName.split(" ")[1] || currentUser.shortName}`,
          Email: currentUser.email,
          Title: currentUser.displayName,
          ID: currentUser.id,
          Department: "",
          Position: "",
          UserID: currentUser.id,
        },
        role: [{ id: "1", name: "Владелец" }],
      });
    }
  }, [currentUser, participants.length]);

  // Пересчет `isMustDisabled` после изменения `participants`
  useEffect(() => {
    // Обновляем участников, чтобы пересчитать дизейбл
    participants.forEach((participant, index) => {
      const isOwner = participant.role[0].id === "1";
      const shouldDisable = isOwner && !hasMultipleOwners(participants) && !!participant.user;
      if (shouldDisable !== (fields[index] as Participant)?.isDisabled) {
        update(index, { ...fields[index], isDisabled: shouldDisable });
      }
    });
  }, [participants]);

  return (
    <div className="p-5 bg-gray-100 rounded-lg">
      {fields.map((item, index) => {
        const participantItem = item as Participant;
        const isMustDisabled =
          participantItem.role[0].id === "1" && !hasMultipleOwners(participants) && !!participantItem.user;

        return (
          <FlexRow className={"gap-x-5"} key={item.id}>
            <FlexCol className={twMerge("justify-center", participants.length > 1 ? "w-[50%]" : "w-1/2")}>
              <Controller
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value, name, ref, disabled }, fieldState: { error } }) => {
                  return (
                    <SearchInputEmployee
                      error={!!error}
                      required={true}
                      unique={participants.filter((p) => p.user).map((p) => p.user.Email)}
                      selectedPerson={value}
                      changePerson={(user) => handleUserChange(user, index)}
                      disabled={disabled || isMustDisabled}
                    />
                  );
                }}
                name={`participants[${index}].user` as const}
                control={control}
              />
            </FlexCol>
            <FlexCol className={twMerge("justify-center", participants.length > 1 ? "w-[47%]" : "w-1/2")}>
              <Controller
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value, name, ref, disabled }, fieldState: { error } }) => (
                  <TGSelectPickerByList
                    values={value}
                    onChange={(ev) => handleRoleChange(ev, index)}
                    fetchKey="role"
                    classNames={{
                      selectClassName: "!px-5 !shadow-custom",
                      labelClassName: "text-base font-normal",
                      dropdownClassName: "",
                    }}
                    searchPlaceholder="Найти периодичность"
                    dataField={{
                      placeholder: "Выберите",
                      type: "peoplePicker",
                      require: true,
                      hideRemoveButton: true,
                      disabled: disabled || isMustDisabled,
                      title: "Роль сотрудника",
                      width: "w-full",
                      key: "role",
                      single: true,
                    }}
                    list={[
                      { id: "1", name: "Владелец" },
                      { id: "2", name: "Участник" },
                    ]}
                    errors={{}}
                  />
                )}
                name={`participants[${index}].role`}
                control={control}
              />
              <Modal
                zIndex={70}
                open={openConfirmChangeRoleModal}
                toggleDialog={(state) => closeConfirmChangeRoleModal(state)}
                modalClassName="min-h-0 max-w-[600px]"
              >
                <header className="px-10 pb-4">
                  <h2>Подтвердите действие</h2>
                </header>

                <div className="px-10 py-5 grow">
                  <p>
                    Вы уверены, что хотите перестать быть владельцем? <br />
                  </p>
                  <p>Вы не сможете менять настройки совещания.</p>
                </div>

                <footer className="flex flex-col gap-2 px-10 pt-10 mt-4 border-t sm:flex-row sm:justify-between">
                  <TGButton type="button" onClick={() => closeConfirmChangeRoleModal(false)}>
                    Отмена
                  </TGButton>
                  <TGButton type="button" color="bluedark" onClick={submitChangeRole}>
                    Подтвердить
                  </TGButton>
                </footer>
              </Modal>
            </FlexCol>
            {fields.length > 1 && (
              <FlexCol className={twMerge("justify-center", participants.length > 1 ? "w-[3%]" : "w-0")}>
                <TGButton
                  type="button"
                  disabled={disabled || isMustDisabled}
                  icon={<TrashIcon width={20} height={20} />}
                  className="translate-y-[6px] bg-white shadow-custom"
                  onClick={() => removeParticipantById(item.id)}
                />
              </FlexCol>
            )}
          </FlexRow>
        );
      })}
      {!disabled && (
        <TGButton
          className="w-full"
          type="button"
          icon={<PlusCircleIcon width={20} height={20} />}
          onClick={addParticipant}
        >
          Добавить участника
        </TGButton>
      )}
      {/* Архивирование совещания */}
    </div>
  );
}

function RegulationsTor() {
  const [open, setOpen] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);

  const [isModified, setIsModified] = useState(false);

  const {
    getValues,
    setValue,
    formState: { disabled },
  } = useFormContext();

  function toggleAddFormRegulation(state: boolean) {
    setOpen(state);
  }

  const torValues = getValues("tor");

  function submitTor(value: Record<string, any>) {
    // Object.keys(dirtyFields.tor).every((key)=>dirtyFields.tor[key] !== value[key])

    // const torValues = getValues("tor");

    const isFieldsDirty =
      torValues &&
      (torValues.goal !== value.goal ||
        torValues.owner !== value.owner ||
        torValues.participants !== value.participants ||
        torValues.period !== value.period ||
        torValues.introduction !== value.introduction ||
        torValues.kpi !== value.kpi ||
        torValues.results !== value.results);

    const isFieldMeetingsPlanDirty = !compareObjects(torValues, value, "meetingsPlan");

    if (isFieldsDirty || isFieldMeetingsPlanDirty) {
      setValue("tor", value);
      setIsModified(true);
    }

    toggleAddFormRegulation(false);
  }

  function cancelTor() {
    setOpenCancelModal(true);
  }

  useEffect(() => {
    const isFieldsDirty =
      torValues &&
      (torValues.goal ||
        torValues.owner ||
        torValues.participants ||
        torValues.period ||
        torValues.introduction ||
        torValues.kpi ||
        torValues.results);

    const isFieldMeetingsPlanEmpty = isEmpty(torValues.meetingsPlan);

    if (!isFieldMeetingsPlanEmpty || !!isFieldsDirty) {
      setIsModified(true);
    } else {
      setIsModified(false);
    }
  }, [torValues]);

  return (
    <div>
      <TGButton
        type="button"
        className="w-full"
        icon={isModified ? undefined : <PlusCircleIcon width={20} height={20} />}
        onClick={() => toggleAddFormRegulation(true)}
      >
        {isModified ? "Открыть TOR" : "Заполнить TOR"}
      </TGButton>
      <DrawerComponent open={open} toggleDrawer={cancelTor} percentage="79">
        <FlexCol className="p-10 shrink">
          <h1 className="mb-8 text-black">Регламент совещания (TOR)</h1>

          <RegulationsTorForm
            initial={getValues("tor")}
            isModified={isModified}
            onCancel={cancelTor}
            onSubmit={submitTor}
            disabled={disabled}
          />
        </FlexCol>
        <CancelModal
          rightButtonTitle="Вернуться к TOR"
          open={openCancelModal}
          toggleCancel={setOpenCancelModal}
          leftButton={() => {
            setOpenCancelModal(false);
            toggleAddFormRegulation(false);
          }}
          rightButton={() => {
            setOpenCancelModal(false);
          }}
        />
      </DrawerComponent>
    </div>
  );
}

function RegulationsTorForm({
  initial = {
    goal: "",
    owner: "",
    participants: "",
    period: "",
    introduction: "",
    results: "",
    kpi: "",
    meetingsPlan: [],
  },
  onCancel,
  onSubmit,
  isModified,
  disabled,
}: {
  initial?: Record<string, any>;
  onCancel: () => void;
  onSubmit: (state: Record<string, any>) => void;
  isModified: boolean;
  disabled: boolean;
}) {
  const [isChanged, setIsChanged] = useState(false);
  const [torObject, setTorObject] = useState(initial);
  const { getValues } = useFormContext();

  const formValues = getValues(["location", "name"]);

  function changeTor(key: string, value: any) {
    setTorObject((prev) => ({ ...prev, [key]: value }));
  }

  useEffect(() => {
    if (isModified && !_.isEqual(initial, torObject)) {
      setIsChanged(true);
    } else {
      setIsChanged(false);
    }
  }, [torObject, initial, isModified]);

  const isLogicallyEmpty = _.every(torObject, (value) => {
    return _.isEmpty(value);
  });

  return (
    <>
      <div className="mb-5">
        <SearchUI
          search={false}
          type="text"
          textArea={true}
          heightAuto={true}
          disabled={disabled}
          placeholder={"Введите текст"}
          defaultValue={torObject.goal}
          onClick={(value) => changeTor("goal", value)}
          label={"Цель"}
          inputClass={"appearance-none"}
          labelClass="font-medium text-black"
          resetHandle={() => changeTor("goal", "")}
        />
      </div>
      <section className="grid grid-cols-3 gap-5 mb-5">
        <div>
          <SearchUI
            search={false}
            type="text"
            placeholder={"Введите текст"}
            defaultValue={torObject.owner}
            onClick={(value) => changeTor("owner", value)}
            label={"Владелец"}
            disabled={disabled}
            inputClass={"appearance-none"}
            labelClass="font-medium text-black"
            resetHandle={() => changeTor("owner", "")}
          />
        </div>
        <div>
          <SearchUI
            search={false}
            type="text"
            placeholder={"Введите текст"}
            defaultValue={torObject.participants}
            onClick={(value) => changeTor("participants", value)}
            label={"Участники"}
            disabled={disabled}
            inputClass={"appearance-none"}
            labelClass="font-medium text-black"
            resetHandle={() => changeTor("participants", "")}
          />
        </div>
        <div>
          <SearchUI
            search={false}
            type="text"
            placeholder={"Введите текст"}
            defaultValue={torObject.period}
            onClick={(value) => changeTor("period", value)}
            label={"Время и периодичность"}
            disabled={disabled}
            inputClass={"appearance-none"}
            labelClass="font-medium text-black"
            resetHandle={() => changeTor("period", "")}
          />
        </div>
      </section>

      <section className="grid grid-cols-3 gap-5 mb-5">
        <div>
          <SearchUI
            // ref={textAreaRef}
            search={false}
            type="text"
            textArea={true}
            heightAuto={true}
            placeholder={"Введите текст"}
            defaultValue={torObject.introduction}
            onClick={(value) => changeTor("introduction", value)}
            label={"Вводные к встрече"}
            disabled={disabled}
            inputClass={`appearance-none`}
            labelClass="font-medium text-black"
            resetHandle={() => changeTor("introduction", "")}
          />
        </div>

        <div>
          <SearchUI
            search={false}
            type="text"
            textArea={true}
            heightAuto={true}
            placeholder={"Введите текст"}
            defaultValue={torObject.results}
            onClick={(value) => changeTor("results", value)}
            label={"Итоги"}
            disabled={disabled}
            inputClass={"appearance-none"}
            labelClass="font-medium text-black"
            resetHandle={() => changeTor("results", "")}
          />
        </div>
        <div>
          <SearchUI
            search={false}
            type="text"
            // require={true}
            // error={invalid}
            textArea={true}
            heightAuto={true}
            placeholder={"Введите текст"}
            defaultValue={torObject.kpi}
            onClick={(value) => changeTor("kpi", value)}
            label={"Уровень KPI отчета"}
            disabled={disabled}
            inputClass={"appearance-none"}
            labelClass="font-medium text-black"
            resetHandle={() => changeTor("kpi", "")}
          />
        </div>
      </section>
      <section>
        <h3 className="text-[#637381] font-medium mb-2.5">План встречи</h3>
        <MeetingsPlanTable torObject={torObject} changeTor={changeTor} disabled={disabled} />
      </section>
      <FlexRow className="space-x-2.5 justify-between mt-10">
        {isModified ? (
          <>
            <FlexCol>
              {isChanged && <TGButton onClick={() => setTorObject(initial)}>Отменить изменения</TGButton>}
            </FlexCol>
            <FlexCol>
              <FlexRow className="space-x-2.5">
                <FlexCol>
                  <DownloadPDFButton
                    data={{
                      goal: torObject.goal,
                      introduction: torObject.introduction,
                      kpi: torObject.kpi,
                      results: torObject.results,
                      owner: torObject.owner,
                      participants: torObject.participants,
                      period: torObject.period,
                      location: formValues[0][0]?.name || "",
                      title: formValues[1] || "",
                      meetingPlans: torObject.meetingsPlan,
                    }}
                  />
                </FlexCol>
                {isChanged && (
                  <FlexCol>
                    <TGButton color="bluedark" onClick={() => onSubmit(torObject)}>
                      Сохранить изменения
                    </TGButton>
                  </FlexCol>
                )}
              </FlexRow>
            </FlexCol>
          </>
        ) : (
          !disabled && (
            <>
              <FlexCol>
                <TGButton type="button" onClick={onCancel}>
                  Отмена
                </TGButton>
              </FlexCol>
              <FlexCol>
                <TGButton
                  disabled={isLogicallyEmpty}
                  type="button"
                  color="bluedark"
                  onClick={() => onSubmit(torObject)}
                >
                  Сохранить TOR
                </TGButton>
              </FlexCol>
            </>
          )
        )}
      </FlexRow>
    </>
  );
}

type TableHeadType = "ID" | "Description" | "Hyperlink" | "Responsible" | "Config";

export type TableBodyItem = {
  ID: number;
  Index: number;
  Description: string;
  Hyperlink: string;
  Responsible: string;
};

const tableHeaderContent: Record<TableHeadType, TableColumnWithIcon> = {
  ID: {
    title: "№",
    sort: false,
    style: { width: "3%" },
  },
  Description: {
    title: "Описание",
    sort: false,
    style: { width: "28%" },
  },
  Hyperlink: {
    title: "Гиперссылка",
    sort: false,
    style: { width: "28%" },
  },
  Responsible: {
    title: "Ответственный",
    sort: false,
    style: { width: "28%" },
  },
  Config: {
    title: "",
    sort: false,
    style: { width: "8%" },
  },
};

function MeetingsPlanTable({
  torObject,
  changeTor,
  disabled,
}: {
  torObject: Record<string, any>;
  changeTor: (key: string, value: any) => void;
  disabled: boolean;
}) {
  const [items, setItems] = useState<TableBodyItem[]>([]);
  const [openAddItem, setOpenAddItem] = useState(false);

  function toggleOpenItem(value: boolean) {
    setOpenAddItem(value);
  }

  function submitItem(item: TableBodyItem) {
    const tempItems = [...items, item];
    setItems(tempItems);
    toggleOpenItem(false);

    changeTor("meetingsPlan", tempItems);
  }

  function editItem(item: TableBodyItem) {
    const mapped = items.map((d) => (d.ID === item.ID ? item : d));

    setItems(mapped);
    changeTor("meetingsPlan", mapped);
  }

  function removeItem(item: TableBodyItem) {
    const removed = items.filter((d) => d.ID !== item.ID).map((item, index) => ({ ...item, Index: index }));

    setItems(removed);

    changeTor("meetingsPlan", removed);
  }

  useEffect(() => {
    const values = torObject?.meetingsPlan || [];

    if (values) {
      setItems(values);
    }
  }, [torObject.meetingsPlan]);

  return (
    <FlexRow className="px-6 py-5 overflow-x-auto bg-white rounded-lg table-custom shadow-custom scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-thumb-[#D1D5DB]">
      <FlexCol className="divide-y min-w-[945px] grow">
        <FlexRow className="gap-x-4 pb-5 text-black">
          {Object.keys(tableHeaderContent).map((fieldKey) => {
            const key = fieldKey as TableHeadType;

            return (
              <TableHeaderColumn key={fieldKey} tableHeaderContent={tableHeaderContent[key]} sortDirection="desc" />
            );
          })}
        </FlexRow>
        {items.map((item, index) => (
          <Item
            key={item.ID}
            item={item}
            remove={removeItem}
            edit={editItem}
            disabled={disabled}
            itemsLength={items.length}
          />
        ))}

        {openAddItem ? (
          <ChangeItem toggleOpen={() => toggleOpenItem(false)} onSubmit={submitItem} itemsLength={items.length} />
        ) : disabled ? null : (
          <TGButton
            type="button"
            className="w-full"
            icon={<PlusCircleIcon width={20} height={20} />}
            onClick={() => toggleOpenItem(true)}
          >
            Добавить пункт
          </TGButton>
        )}
      </FlexCol>
    </FlexRow>
  );
}

function Item({
  item,
  remove,
  edit,
  disabled,
  itemsLength,
}: {
  item: TableBodyItem;
  remove: (item: TableBodyItem) => void;
  edit: (item: TableBodyItem) => void;
  disabled: boolean;
  itemsLength: number;
}) {
  const [openEditItem, setOpenEditItem] = useState(false);

  function toggleOpenEditItem(value: boolean) {
    setOpenEditItem(value);
  }

  function editItemHandler(item: TableBodyItem) {
    edit(item);
    setOpenEditItem(false);
  }

  return (
    <>
      {openEditItem ? (
        <ChangeItem
          toggleOpen={() => toggleOpenEditItem(false)}
          onSubmit={editItemHandler}
          initial={item}
          itemsLength={itemsLength}
        />
      ) : (
        <FlexRow
          className="py-4 hover:bg-[#F3F4F6] hover:bg-opacity-50 rounded-lg  cursor-pointer gap-x-4 -mx-2 px-2 items-center"
          key={item.ID}
        >
          <FlexCol className="break-words text-[#637381] justify-center" style={{ width: "3%" }}>
            {item.Index + 1}
          </FlexCol>
          <FlexCol className="break-words text-darkblue" style={{ width: "28%" }}>
            {item.Description}
          </FlexCol>
          <FlexCol className="break-words text-[#637381]" style={{ width: "28%" }}>
            <a href={item.Hyperlink} rel="noreferrer" target="_blank">
              {item.Hyperlink}
            </a>
          </FlexCol>
          <FlexCol className="break-words text-[#637381]" style={{ width: "28%" }}>
            {item.Responsible}
          </FlexCol>
          <FlexCol className="break-words text-[#637381]" style={{ width: "8%" }}>
            {disabled ? null : (
              <FlexRow className="space-x-2.5">
                <FlexCol>
                  <TGButton
                    type="button"
                    color="gray"
                    icon={<PencilIcon width={24} height={24} />}
                    className="w-10 h-10"
                    onClick={() => toggleOpenEditItem(true)}
                  />
                </FlexCol>
                <FlexCol>
                  <TGButton
                    type="button"
                    color="gray"
                    icon={<TrashIcon width={24} height={24} />}
                    className="w-10 h-10"
                    onClick={() => remove(item)}
                  />
                </FlexCol>
              </FlexRow>
            )}
          </FlexCol>
        </FlexRow>
      )}
    </>
  );
}

function ChangeItem({
  initial,
  onSubmit,
  toggleOpen,
  itemsLength,
}: {
  initial?: TableBodyItem;
  onSubmit: (item: TableBodyItem) => void;
  toggleOpen: (value: boolean) => void;
  itemsLength: number;
}) {
  const [item, setItem] = useState({
    ID: Date.now(),
    Description: "",
    Hyperlink: "",
    Responsible: "",
    Index: itemsLength,
  });

  function changeItem(key: string, value: string) {
    setItem((prev) => ({ ...prev, [key]: value }));
  }

  useEffect(() => {
    if (initial && (initial.Description || initial.Hyperlink || initial.Responsible)) {
      setItem(initial);
    }
  }, [initial]);

  const isSaveButtonDisabled = useMemo(() => !!item.Description || !!item.Hyperlink || !!item.Responsible, [item]);

  return (
    <div className="p-5 space-y-5 bg-gray-100 rounded-lg">
      <FlexRow className="space-x-5">
        <FlexCol className="w-1/3">
          <SearchUI
            delay={0}
            label="Описание"
            defaultValue={item.Description}
            onClick={(value) => changeItem("Description", value)}
            placeholder="Введите текст"
            search={false}
            inputClass={"appearance-none"}
            labelClass="font-medium text-black"
          />
        </FlexCol>
        <FlexCol className="w-1/3">
          <SearchUI
            delay={0}
            label="Гиперссылка"
            defaultValue={item.Hyperlink}
            onClick={(value) => changeItem("Hyperlink", value)}
            placeholder="Введите текст"
            search={false}
            inputClass={"appearance-none"}
            labelClass="font-medium text-black"
          />
        </FlexCol>
        <FlexCol className="w-1/3">
          <SearchUI
            delay={0}
            label="Ответственный"
            defaultValue={item.Responsible}
            onClick={(value) => changeItem("Responsible", value)}
            placeholder="Введите текст"
            search={false}
            inputClass={"appearance-none"}
            labelClass="font-medium text-black"
          />
        </FlexCol>
      </FlexRow>
      <FlexRow className="space-x-5">
        <FlexCol className="w-1/2">
          <TGButton onClick={() => toggleOpen(false)}>Отмена</TGButton>
        </FlexCol>
        <FlexCol className="w-1/2">
          <TGButton color="bluedark" disabled={!isSaveButtonDisabled} onClick={() => onSubmit(item)}>
            Сохранить
          </TGButton>
        </FlexCol>
      </FlexRow>
    </div>
  );
}

export function TableHeaderColumn({ tableHeaderContent, sortDirection, onClick }: TableHeaderColumnProps) {
  return (
    <FlexCol className="select-none" style={tableHeaderContent.style}>
      <FlexRow className="gap-x-2 items-center">
        {tableHeaderContent.icon && <FlexCol>{tableHeaderContent.icon}</FlexCol>}
        <FlexCol className="font-medium whitespace-pre-line w-fit">{tableHeaderContent.title}</FlexCol>
        {tableHeaderContent.sort && (
          <FlexCol className="w-auto cursor-pointer" onClick={onClick}>
            <SortIcon sortDirection={sortDirection} />
          </FlexCol>
        )}
      </FlexRow>
    </FlexCol>
  );
}

export function hasMultipleOwners(participants: Participant[]) {
  // Используем filter, чтобы найти объекты с ролью "Владелец"
  const ownerCount = participants.filter(
    (participant) => participant.role && participant.role.some((r) => r.name === "Владелец") && participant.user,
  ).length;

  // Проверяем, есть ли хотя бы два таких объекта
  return ownerCount >= 2;
}
